import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Check, X, Save, Eye } from 'lucide-react';
import { alertasAPI } from '../services/api';
import { useConfirm, Tooltip, Modal, Button } from './ui';

const AlertasPanel = () => {
    const confirm = useConfirm();
    const [subTab, setSubTab] = useState('config');
    const [alertasConfig, setAlertasConfig] = useState([]);
    const [tiposActividad, setTiposActividad] = useState([]);
    const [smtpConfig, setSmtpConfig] = useState({
        host: '', port: 587, secure: false, auth_user: '', auth_password: '',
        from_name: 'Don Presupuesto', from_email: '', activo: false
    });
    const [templates, setTemplates] = useState([]);
    const [nuevoTipo, setNuevoTipo] = useState('');
    const [testEmail, setTestEmail] = useState('');
    const [sendingTest, setSendingTest] = useState(false);
    const [savingSmtp, setSavingSmtp] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);

    // Cambios pendientes
    const [pendingAlertas, setPendingAlertas] = useState({});
    const [pendingTipos, setPendingTipos] = useState({});
    const [pendingTemplates, setPendingTemplates] = useState({});

    const getAlertaVal = (alerta, field) => (
        pendingAlertas[alerta.id]?.[field] !== undefined ? pendingAlertas[alerta.id][field] : alerta[field] ?? ''
    );
    const setAlertaVal = (id, field, value) => {
        setPendingAlertas(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    };
    const hasAlertaChanges = (id) => Boolean(pendingAlertas[id] && Object.keys(pendingAlertas[id]).length > 0);
    const confirmAlertaChanges = async (id) => {
        const changes = pendingAlertas[id];
        if (!changes) return;
        try {
            await alertasAPI.updateConfig(id, changes);
            setAlertasConfig(prev => prev.map(a => a.id === id ? { ...a, ...changes } : a));
            setPendingAlertas(prev => { const n = { ...prev }; delete n[id]; return n; });
            toast.success('Configuración actualizada');
        } catch (err) {
            toast.error('Error al actualizar');
        }
    };
    const discardAlertaChanges = (id) => {
        setPendingAlertas(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    const getTipoVal = (tipo) => (
        pendingTipos[tipo.id] !== undefined ? pendingTipos[tipo.id] : tipo.nombre ?? ''
    );
    const hasTipoChanges = (id) => pendingTipos[id] !== undefined;
    const confirmTipoChanges = async (id) => {
        const nuevo = pendingTipos[id];
        if (!nuevo || !nuevo.trim()) return;
        try {
            await alertasAPI.updateTipoActividad(id, { nombre: nuevo.trim() });
            setTiposActividad(prev => prev.map(t => t.id === id ? { ...t, nombre: nuevo.trim() } : t));
            setPendingTipos(prev => { const n = { ...prev }; delete n[id]; return n; });
            toast.success('Tipo actualizado');
        } catch (err) {
            toast.error('Error al actualizar');
        }
    };
    const discardTipoChanges = (id) => {
        setPendingTipos(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    const getTplVal = (tpl, field) => (
        pendingTemplates[tpl.id]?.[field] !== undefined ? pendingTemplates[tpl.id][field] : tpl[field] ?? ''
    );
    const setTplVal = (id, field, value) => {
        setPendingTemplates(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    };
    const hasTplChanges = (id) => Boolean(pendingTemplates[id] && Object.keys(pendingTemplates[id]).length > 0);
    const confirmTplChanges = async (id) => {
        const changes = pendingTemplates[id];
        if (!changes) return;
        try {
            for (const [campo, valor] of Object.entries(changes)) {
                await alertasAPI.updateTemplate(id, { [campo]: valor });
            }
            setTemplates(prev => prev.map(t => t.id === id ? { ...t, ...changes } : t));
            setPendingTemplates(prev => { const n = { ...prev }; delete n[id]; return n; });
            toast.success('Template guardado');
        } catch (err) {
            toast.error('Error al guardar template');
        }
    };
    const discardTplChanges = (id) => {
        setPendingTemplates(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    const sampleData = {
        numero: 'COT-0001',
        cliente: 'Empresa Demo S.A.C.',
        responsable: 'Juan Pérez',
        dias: '5',
        ultima_actividad: 'Llamada telefónica: Se conversó con el cliente sobre el presupuesto',
        fecha_ultima_actividad: '03/02/2026'
    };

    const replaceVariables = (text) => {
        let result = text;
        for (const [key, value] of Object.entries(sampleData)) {
            result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
        }
        return result;
    };

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        try {
            const [configRes, tiposRes, smtpRes, templatesRes] = await Promise.all([
                alertasAPI.getConfig(),
                alertasAPI.getTiposActividad(),
                alertasAPI.getSmtp(),
                alertasAPI.getTemplates()
            ]);
            setAlertasConfig(configRes.data || []);
            setTiposActividad(tiposRes.data || []);
            if (smtpRes.data) setSmtpConfig(smtpRes.data);
            setTemplates(templatesRes.data || []);
        } catch (err) {
            console.error('Error al cargar datos de alertas:', err);
            toast.error('Error al cargar configuración de alertas');
        }
    };

    // ============ ALERTAS CONFIG ============
    const actualizarAlerta = async (id, campo, valor) => {
        try {
            await alertasAPI.updateConfig(id, { [campo]: valor });
            setAlertasConfig(prev => prev.map(a => a.id === id ? { ...a, [campo]: valor } : a));
            toast.success('Configuración actualizada');
        } catch (err) {
            toast.error('Error al actualizar');
        }
    };

    // ============ TIPOS ACTIVIDAD ============
    const agregarTipo = async () => {
        if (!nuevoTipo.trim()) return;
        try {
            const res = await alertasAPI.createTipoActividad({ nombre: nuevoTipo.trim() });
            setTiposActividad(prev => [...prev, res.data]);
            setNuevoTipo('');
            toast.success('Tipo de actividad agregado');
        } catch (err) {
            toast.error('Error al agregar');
        }
    };

    const eliminarTipo = async (id, nombre) => {
        const ok = await confirm({
            title: '¿Eliminar tipo de actividad?',
            message: <p>Vas a eliminar <strong>{nombre}</strong>. Esta acción no se puede deshacer.</p>,
            confirmText: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await alertasAPI.deleteTipoActividad(id);
            setTiposActividad(prev => prev.filter(t => t.id !== id));
            toast.success('Tipo eliminado');
        } catch (err) {
            toast.error(err.message || 'Error al eliminar');
        }
    };

    const toggleTipo = async (id, activo) => {
        try {
            await alertasAPI.updateTipoActividad(id, { activo: activo ? 0 : 1 });
            setTiposActividad(prev => prev.map(t => t.id === id ? { ...t, activo: activo ? 0 : 1 } : t));
        } catch (err) {
            toast.error('Error al actualizar');
        }
    };

    // ============ SMTP ============
    const guardarSmtp = async () => {
        setSavingSmtp(true);
        try {
            await alertasAPI.updateSmtp(smtpConfig);
            toast.success('Configuración SMTP guardada');
        } catch (err) {
            toast.error('Error al guardar SMTP');
        } finally {
            setSavingSmtp(false);
        }
    };

    const enviarEmailPrueba = async () => {
        if (!testEmail.trim()) {
            toast.error('Ingresa un email de prueba');
            return;
        }
        setSendingTest(true);
        try {
            await alertasAPI.testSmtp(testEmail);
            toast.success('Email de prueba enviado');
        } catch (err) {
            toast.error(err.message || 'Error al enviar email de prueba');
        } finally {
            setSendingTest(false);
        }
    };

    // ============ TEMPLATES ============
    const actualizarTemplate = async (id, campo, valor) => {
        try {
            await alertasAPI.updateTemplate(id, { [campo]: valor });
            setTemplates(prev => prev.map(t => t.id === id ? { ...t, [campo]: valor } : t));
            toast.success('Template actualizado');
        } catch (err) {
            toast.error('Error al actualizar template');
        }
    };

    return (
        <div className="admin-content">
            {/* Sub-tabs de alertas */}
            <div className="alertas-subtabs">
                <button
                    className={`alertas-subtab ${subTab === 'config' ? 'active' : ''}`}
                    onClick={() => setSubTab('config')}
                >
                    Estados y Umbrales
                </button>
                <button
                    className={`alertas-subtab ${subTab === 'tipos' ? 'active' : ''}`}
                    onClick={() => setSubTab('tipos')}
                >
                    Tipos de Actividad
                </button>
                <button
                    className={`alertas-subtab ${subTab === 'smtp' ? 'active' : ''}`}
                    onClick={() => setSubTab('smtp')}
                >
                    Correo SMTP
                </button>
                <button
                    className={`alertas-subtab ${subTab === 'templates' ? 'active' : ''}`}
                    onClick={() => setSubTab('templates')}
                >
                    Templates Email
                </button>
            </div>

            {/* SUB-TAB: Configuración de estados */}
            {subTab === 'config' && (
                <div className="alertas-section">
                    <h3 className="h3">Estados de Alerta</h3>
                    <p style={{ color: 'var(--muted)', fontSize: '13px', marginBottom: '16px' }}>
                        Configura los colores y umbrales de días para cada estado de seguimiento.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {alertasConfig.map(alerta => {
                            const dirty = hasAlertaChanges(alerta.id);
                            const currentColor = getAlertaVal(alerta, 'color');
                            const currentNombre = getAlertaVal(alerta, 'nombre');
                            const currentDias = getAlertaVal(alerta, 'dias_umbral');
                            return (
                            <div key={alerta.id} className={`alerta-config-card ${dirty ? 'has-pending-changes' : ''}`}>
                                <div className="alerta-config-header">
                                    <div
                                        className="alerta-color-dot"
                                        style={{ backgroundColor: currentColor }}
                                    />
                                    <span className="alerta-config-nombre">{currentNombre}</span>
                                </div>

                                <div className="alerta-config-fields">
                                    <label className="field">
                                        <span className="label">Color</span>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <input
                                                type="color"
                                                value={currentColor}
                                                onChange={(e) => setAlertaVal(alerta.id, 'color', e.target.value)}
                                                style={{ width: '40px', height: '32px', border: 'none', cursor: 'pointer', background: 'transparent' }}
                                            />
                                            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{currentColor}</span>
                                        </div>
                                    </label>

                                    <label className="field">
                                        <span className="label">Días umbral</span>
                                        <input
                                            className="input"
                                            type="number"
                                            min="1"
                                            value={currentDias}
                                            onChange={(e) => setAlertaVal(alerta.id, 'dias_umbral', parseInt(e.target.value) || 1)}
                                            style={{ width: '80px' }}
                                        />
                                    </label>

                                    <label className="field">
                                        <span className="label">Nombre</span>
                                        <input
                                            className="input"
                                            type="text"
                                            value={currentNombre}
                                            onChange={(e) => setAlertaVal(alerta.id, 'nombre', e.target.value)}
                                        />
                                    </label>
                                </div>

                                <div className="alerta-config-actions">
                                    <button
                                        className="btn success small"
                                        type="button"
                                        onClick={() => confirmAlertaChanges(alerta.id)}
                                        disabled={!dirty}
                                    >
                                        <Save size={14} />
                                        Actualizar
                                    </button>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* SUB-TAB: Tipos de actividad */}
            {subTab === 'tipos' && (
                <div className="alertas-section">
                    <h3 className="h3">Tipos de Actividad</h3>
                    <p style={{ color: 'var(--muted)', fontSize: '13px', marginBottom: '16px' }}>
                        Define los tipos de comunicación disponibles al registrar actividad en cotizaciones.
                    </p>

                    <div className="form-row" style={{ marginBottom: '16px' }}>
                        <label className="field" style={{ flex: 1 }}>
                            <span className="label">Nuevo tipo de actividad</span>
                            <input
                                className="input"
                                type="text"
                                value={nuevoTipo}
                                onChange={(e) => setNuevoTipo(e.target.value)}
                                placeholder="Ej: Visita a oficina"
                                onKeyDown={(e) => e.key === 'Enter' && agregarTipo()}
                            />
                        </label>
                        <button
                            className="btn neon"
                            type="button"
                            onClick={agregarTipo}
                            style={{ alignSelf: 'flex-end' }}
                        >
                            + Agregar
                        </button>
                    </div>

                    <div className="admin-list">
                        {tiposActividad.map(tipo => {
                            const dirty = hasTipoChanges(tipo.id);
                            return (
                            <div key={tipo.id} className={`admin-item ${!tipo.activo ? 'inactive' : ''} ${dirty ? 'has-pending-changes' : ''}`}>
                                <div className="admin-item-content">
                                    <input
                                        className="input"
                                        type="text"
                                        value={getTipoVal(tipo)}
                                        onChange={(e) => setPendingTipos(prev => ({ ...prev, [tipo.id]: e.target.value }))}
                                    />
                                </div>
                                <div className="admin-item-actions">
                                    {dirty && (
                                        <>
                                            <button
                                                className="btn success small"
                                                onClick={() => confirmTipoChanges(tipo.id)}
                                            >
                                                <Check size={14} />
                                                Actualizar
                                            </button>
                                            <Tooltip content="Descartar cambios">
                                                <button
                                                    className="btn ghost small"
                                                    onClick={() => discardTipoChanges(tipo.id)}
                                                    aria-label="Descartar cambios"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </Tooltip>
                                        </>
                                    )}
                                    <button
                                        className={`btn ghost small ${tipo.activo ? '' : 'success'}`}
                                        onClick={() => toggleTipo(tipo.id, tipo.activo)}
                                    >
                                        {tipo.activo ? 'Desactivar' : 'Activar'}
                                    </button>
                                    <button
                                        className="btn ghost small danger"
                                        onClick={() => eliminarTipo(tipo.id, tipo.nombre)}
                                    >
                                        Eliminar
                                    </button>
                                </div>
                            </div>
                            );
                        })}
                        {tiposActividad.length === 0 && (
                            <p className="empty-message">No hay tipos de actividad creados</p>
                        )}
                    </div>
                </div>
            )}

            {/* SUB-TAB: SMTP */}
            {subTab === 'smtp' && (
                <div className="alertas-section">
                    <h3 className="h3">Configuración de Correo (SMTP)</h3>
                    <p style={{ color: 'var(--muted)', fontSize: '13px', marginBottom: '16px' }}>
                        Configura el servidor de correo para enviar alertas automáticas.
                    </p>

                    <div className="smtp-form">
                        <div className="form-row">
                            <label className="field" style={{ flex: 2 }}>
                                <span className="label">Servidor SMTP</span>
                                <input
                                    className="input"
                                    type="text"
                                    value={smtpConfig.host}
                                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, host: e.target.value }))}
                                    placeholder="smtp.gmail.com"
                                />
                            </label>
                            <label className="field" style={{ flex: 1 }}>
                                <span className="label">Puerto</span>
                                <input
                                    className="input"
                                    type="number"
                                    value={smtpConfig.port}
                                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, port: parseInt(e.target.value) || 587 }))}
                                />
                            </label>
                            <label className="field" style={{ flex: 1 }}>
                                <span className="label">Seguro (SSL)</span>
                                <div style={{ paddingTop: '8px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={smtpConfig.secure}
                                            onChange={(e) => setSmtpConfig(prev => ({ ...prev, secure: e.target.checked }))}
                                        />
                                        <span style={{ fontSize: '13px', color: 'var(--text)' }}>
                                            {smtpConfig.secure ? 'SSL activo' : 'TLS (STARTTLS)'}
                                        </span>
                                    </label>
                                </div>
                            </label>
                        </div>

                        <div className="form-row">
                            <label className="field" style={{ flex: 1 }}>
                                <span className="label">Usuario SMTP</span>
                                <input
                                    className="input"
                                    type="text"
                                    value={smtpConfig.auth_user}
                                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, auth_user: e.target.value }))}
                                    placeholder="usuario@gmail.com"
                                />
                            </label>
                            <label className="field" style={{ flex: 1 }}>
                                <span className="label">Contraseña SMTP</span>
                                <input
                                    className="input"
                                    type="password"
                                    value={smtpConfig.auth_password}
                                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, auth_password: e.target.value }))}
                                    placeholder="********"
                                />
                            </label>
                        </div>

                        <div className="divider" />

                        <div className="form-row">
                            <label className="field" style={{ flex: 1 }}>
                                <span className="label">Nombre del remitente</span>
                                <input
                                    className="input"
                                    type="text"
                                    value={smtpConfig.from_name}
                                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, from_name: e.target.value }))}
                                    placeholder="Don Presupuesto"
                                />
                            </label>
                            <label className="field" style={{ flex: 1 }}>
                                <span className="label">Email del remitente</span>
                                <input
                                    className="input"
                                    type="email"
                                    value={smtpConfig.from_email}
                                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, from_email: e.target.value }))}
                                    placeholder="alertas@empresa.com"
                                />
                            </label>
                        </div>

                        <div className="form-row" style={{ marginTop: '8px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={smtpConfig.activo}
                                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, activo: e.target.checked }))}
                                />
                                <span style={{ fontSize: '13px', color: 'var(--text)' }}>
                                    Activar envío de alertas por correo
                                </span>
                            </label>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                            <button
                                className="btn neon"
                                onClick={guardarSmtp}
                                disabled={savingSmtp}
                            >
                                {savingSmtp ? 'Guardando...' : 'Guardar configuración'}
                            </button>
                        </div>

                        <div className="divider" />

                        <h4 style={{ color: 'var(--text)', marginBottom: '8px' }}>Enviar email de prueba</h4>
                        <div className="form-row">
                            <label className="field" style={{ flex: 1 }}>
                                <input
                                    className="input"
                                    type="email"
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    placeholder="correo@ejemplo.com"
                                />
                            </label>
                            <button
                                className="btn ghost"
                                onClick={enviarEmailPrueba}
                                disabled={sendingTest}
                                style={{ alignSelf: 'flex-end' }}
                            >
                                {sendingTest ? 'Enviando...' : 'Enviar prueba'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* SUB-TAB: Templates */}
            {subTab === 'templates' && (
                <div className="alertas-section">
                    <h3 className="h3">Templates de Email</h3>
                    <p style={{ color: 'var(--muted)', fontSize: '13px', marginBottom: '16px' }}>
                        Personaliza los emails de alerta. Variables disponibles: {' '}
                        <code style={{ color: 'var(--neon)', fontSize: '12px' }}>
                            {'{{numero}} {{cliente}} {{responsable}} {{dias}} {{ultima_actividad}} {{fecha_ultima_actividad}}'}
                        </code>
                    </p>

                    {templates.map(template => {
                        const dirty = hasTplChanges(template.id);
                        return (
                        <div key={template.id} className={`template-card ${dirty ? 'has-pending-changes' : ''}`}>
                            <div className="template-header">
                                <h4 style={{ margin: 0, color: 'var(--text)' }}>{template.nombre}</h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    <Button
                                        variant="soft"
                                        size="sm"
                                        leftIcon={<Eye size={14} />}
                                        onClick={() => setPreviewTemplate(template)}
                                    >
                                        Vista previa
                                    </Button>
                                    <label className="switch-label">
                                        <div
                                            className={`switch-track ${template.activo ? 'active' : ''}`}
                                            onClick={() => actualizarTemplate(template.id, 'activo', template.activo ? 0 : 1)}
                                        >
                                            <div className="switch-thumb" />
                                        </div>
                                        <span className="switch-text">{template.activo ? 'Activo' : 'Inactivo'}</span>
                                    </label>
                                </div>
                            </div>

                            <label className="field">
                                <span className="label">Asunto</span>
                                <input
                                    className="input"
                                    type="text"
                                    value={getTplVal(template, 'asunto')}
                                    onChange={(e) => setTplVal(template.id, 'asunto', e.target.value)}
                                />
                            </label>

                            <label className="field">
                                <span className="label">Cuerpo (HTML)</span>
                                <textarea
                                    className="input"
                                    rows="8"
                                    value={getTplVal(template, 'cuerpo')}
                                    onChange={(e) => setTplVal(template.id, 'cuerpo', e.target.value)}
                                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                                />
                            </label>

                            <div className="template-actions">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => discardTplChanges(template.id)}
                                    disabled={!dirty}
                                    type="button"
                                >
                                    Descartar
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    leftIcon={<Save size={14} />}
                                    onClick={() => confirmTplChanges(template.id)}
                                    disabled={!dirty}
                                    type="button"
                                >
                                    Guardar cambios
                                </Button>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {/* Modal de Preview — primitivo Modal (size xl ≈ 896px) */}
            <Modal
                open={!!previewTemplate}
                onClose={() => setPreviewTemplate(null)}
                title={previewTemplate ? `Vista previa: ${previewTemplate.nombre}` : ''}
                size="xl"
                footer={
                    <>
                        <p className="text-xs text-muted m-0 flex-1">
                            Datos de ejemplo. Las variables se reemplazan con datos reales al enviar.
                        </p>
                        <Button variant="ghost" onClick={() => setPreviewTemplate(null)}>
                            Cerrar
                        </Button>
                    </>
                }
            >
                {previewTemplate && (
                    <>
                        <div className="mb-3 pb-3 border-b border-line">
                            <span className="text-xs text-muted">Asunto: </span>
                            <span className="text-sm text-text font-medium">
                                {replaceVariables(previewTemplate.asunto)}
                            </span>
                        </div>
                        <div className="rounded-lg p-1" style={{ background: '#f4f4f4' }}>
                            <iframe
                                title="Email Preview"
                                srcDoc={replaceVariables(previewTemplate.cuerpo)}
                                style={{
                                    width: '100%',
                                    height: '420px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    background: '#ffffff'
                                }}
                            />
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default AlertasPanel;
